import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useEducatorProfileFacetDescriptor } from "@/components/search/educator-profile-facet"
import {
  type TermFacetDescriptor,
  useTermFacetDescriptor,
} from "@/components/search/facet-descriptor"
import { FACET_IDS } from "@/components/search/facet-ids"
import { createAttributeOptions } from "@/constants/speciality"

// Educator facets carry the shared identifiers and aggregate their own fields.
const LISTING_TYPE_FIELD = "listing_type.keyword"
const RELIEF_AVAILABILITY_FIELD = "relief_availability.keyword"
const CURRICULUM_FIELD = "nested_classes.curriculum_key"
const SUBJECT_FIELD = "nested_classes.subject_key"
const SPECIALITIES_FIELD = "speciality_attributes.keyword"
const TAGS_FIELD = "tags.keyword"

const LISTING_TYPE_FACET = {
  type: "terms",
  size: 1000,
  field: LISTING_TYPE_FIELD,
}
const RELIEF_AVAILABILITY_FACET = {
  type: "terms",
  size: 1000,
  field: RELIEF_AVAILABILITY_FIELD,
}
const CURRICULUM_FACET = {
  type: "nested_terms",
  size: 1000,
  field: CURRICULUM_FIELD,
  nestedPath: "nested_classes",
}
const SUBJECT_FACET = {
  type: "nested_terms",
  size: 1000,
  field: SUBJECT_FIELD,
  nestedPath: "nested_classes",
}
const SPECIALITIES_FACET = {
  type: "terms",
  size: 1000,
  field: SPECIALITIES_FIELD,
}

/**
 * The educator facets, described once for the public sidebar and for the
 * exported shortlist module, so both offer the same options and labels.
 */
export function useEducatorFacetDescriptors(): TermFacetDescriptor[] {
  return [
    useEducatorListingTypeFacetDescriptor(),
    useEducatorReliefAvailabilityFacetDescriptor(),
    useEducatorCurriculumFacetDescriptor(),
    useEducatorSubjectFacetDescriptor(),
    useEducatorSpecialitiesFacetDescriptor(),
    useEducatorTagsFacetDescriptor(),
  ]
}

export function useEducatorListingTypeFacetDescriptor(): TermFacetDescriptor {
  const { t } = useTranslation("search")
  const { t: tListingType } = useTranslation("search", {
    keyPrefix: "listing_type",
  })
  const label = t("facet.listing_type")

  return useTermFacetDescriptor({
    id: FACET_IDS.listingType,
    config: LISTING_TYPE_FACET,
    label,
    pillLabel: (value) => `${label} ${tListingType(value as never)}`,
    option: (value, count) => ({
      value,
      count,
      label: tListingType(value as never),
    }),
  })
}

export function useEducatorReliefAvailabilityFacetDescriptor(): TermFacetDescriptor {
  const { t } = useTranslation("search")
  const { t: tAvailability } = useTranslation("search", {
    keyPrefix: "relief_availability",
  })
  const label = t("facet.relief_availability")

  return useTermFacetDescriptor({
    id: FACET_IDS.reliefAvailability,
    config: RELIEF_AVAILABILITY_FACET,
    label,
    pillLabel: (value) => `${label} ${tAvailability(value as never)}`,
    option: (value, count) => ({
      value,
      count,
      label: tAvailability(value as never),
    }),
  })
}

export function useEducatorCurriculumFacetDescriptor(): TermFacetDescriptor {
  const { t } = useTranslation("search")
  const { t: tCurricula } = useTranslation("subjects", {
    keyPrefix: "curricula",
  })
  const label = t("facet.curriculum")

  return useTermFacetDescriptor({
    id: FACET_IDS.curriculum,
    config: CURRICULUM_FACET,
    label,
    pillLabel: (value) => `${label} ${tCurricula(value as never)}`,
    searchable: true,
    option: (value, count) => ({
      value,
      count,
      label: tCurricula(value as never),
    }),
  })
}

export function useEducatorSubjectFacetDescriptor(): TermFacetDescriptor {
  const { t } = useTranslation("search")
  const { t: tSubjects } = useTranslation("subjects", { keyPrefix: "subjects" })
  const label = t("facet.subject")

  return useTermFacetDescriptor({
    id: FACET_IDS.subject,
    config: SUBJECT_FACET,
    label,
    pillLabel: (value) => `${label} ${tSubjects(value as never)}`,
    searchable: true,
    option: (value, count) => ({
      value,
      count,
      label: tSubjects(value as never),
    }),
  })
}

export function useEducatorSpecialitiesFacetDescriptor(): TermFacetDescriptor {
  const { t } = useTranslation("search")
  const attributeOptions = useMemo(
    () => createAttributeOptions("search:select_attributes"),
    [],
  )
  const label = t("facet.specialities")
  const translate = (value: string) =>
    attributeOptions.find((option) => option.value === value)?.label ?? value

  return useTermFacetDescriptor({
    id: FACET_IDS.specialities,
    config: SPECIALITIES_FACET,
    label,
    pillLabel: (value) => `${label} ${translate(value)}`,
    searchable: true,
    option: (value, count) => ({ value, count, label: translate(value) }),
  })
}

export function useEducatorTagsFacetDescriptor(): TermFacetDescriptor {
  return useEducatorProfileFacetDescriptor({
    id: FACET_IDS.tags,
    field: TAGS_FIELD,
  })
}
